import { CarouselProps } from '@design-system/carousel';
import {
  ExtendableImage,
  ExtendableImageProps,
} from '@design-system/extendable-image';
import { ProductPageHeaderProps } from '@design-system/product-page-header';
import React, { ReactElement } from 'react';

import Splitter from '~/components/Splitter';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { carouselMapper } from '~/mappers/carousel';
import { imageMapper } from '~/mappers/image';
import {
  breadcrumbsMapper,
  BreadcrumbsUi,
  PageFields,
} from '~/mappers/pageHeader';
import { retailersMapper } from '~/mappers/retailers';
import { SalsifyAssets } from '~/types/common';
import { nlToBr } from '~/utils/stringUtils/stringUtils';

import { variantSelectorMapper } from '../variantSelector';
import {
  HeaderSplitterAdditionalData,
  ProductPageHeaderBasic,
  ProductPageHeaderWithSplitter,
} from './models';

const moveImageToBeginning = (
  images: SalsifyAssets[],
  imageId: string
): SalsifyAssets[] => {
  if (!imageId) {
    return images;
  }

  return images.slice().sort(({ salsifyID: idA }, { salsifyID: idB }) => {
    if (idA === imageId) return -1;
    if (idB === imageId) return 1;

    return 0;
  });
};

const getAlternateImages = (
  images: SalsifyAssets[],
  imageIds: string[]
): SalsifyAssets[] =>
  images.filter((image) => imageIds.includes(image.salsifyID));

const imageElementsMapper = (images: SalsifyAssets[]): ReactElement[] =>
  images.map((image, index) => (
    <ExtendableImage
      key={image.salsifyID}
      {...(imageMapper(
        {
          image: {
            gatsbyImageData: image.localAsset.childImageSharp?.gatsbyImageData,
            url: image.salsifyUrl,
          },
          alt: image.altText,
          objectFit: 'contain',
        },
        index === 0
      ) as ExtendableImageProps)}
    />
  ));

const productPageHeaderCommonMapper = ({
  breadcrumbsUI: [{ hideLastItem, navigationName, overflowButton }],
  breadcrumbs,
  productTitle,
  mode,
  showBreadcrumbs,
}: {
  breadcrumbsUI: BreadcrumbsUi[];
  breadcrumbs: PageFields['breadcrumbs'];
  showBreadcrumbs: boolean | null;
  mode?: string | null;
  productTitle: string;
}) => ({
  breadcrumbs: {
    navigationName,
    hideLastItem,
    items: breadcrumbsMapper(breadcrumbs),
    overflowBtn: {
      children: overflowButton,
    },
    linkComponent: TMPLinkWrap,
  },
  showBreadcrumbs: showBreadcrumbs === null ? true : showBreadcrumbs,
  heading: nlToBr(productTitle),
  mode: mode || undefined,
});

const productPageHeaderBasicMapper = ({
  variantSelector: [variantSelector],
  headerUI: {
    breadcrumbsUI,
    galleryUI: [galleryUI],
    variantNameField,
    showBreadcrumbs,
  },
  productPageHeader,
  fields,
  disclaimer,
  productId,
  pluginInformationText,
  isPriceSpiderAvailable,
  isAdimoAvailable,
  adimoKey,
  adimoButton,
  isMikMakAvailable,
  mikmakAppId,
  mikmakButton,
  retailers: [retailers],
}: ProductPageHeaderBasic): ProductPageHeaderProps => {
  const { productData, breadcrumbs } = fields;

  const {
    descriptionShort,
    productTitle,
    salsifyDigitalAssets,
    heroImageID,
    websiteImagesID,
    barcodeNumber,
  } = productData;

  const websiteImages =
    websiteImagesID &&
    getAlternateImages(salsifyDigitalAssets, websiteImagesID);

  const imageElements = imageElementsMapper(
    websiteImagesID && websiteImagesID.length > 0
      ? moveImageToBeginning(websiteImages, heroImageID)
      : moveImageToBeginning(salsifyDigitalAssets, heroImageID)
  );

  return {
    ...productPageHeaderCommonMapper({
      breadcrumbsUI,
      breadcrumbs,
      showBreadcrumbs,
      mode: productPageHeader?.mode,
      productTitle,
    }),
    description: nlToBr(descriptionShort),
    ...(disclaimer && { disclaimer }),
    ...(retailers && {
      retailersBlock: retailersMapper(retailers, productData),
    }),
    carouselBlock: {
      ...carouselMapper<Omit<CarouselProps, 'items'>>(galleryUI),
      imageElements,
    },
    subHeading: productData[variantNameField],
    ...(variantSelector?.variants?.length > 0 && {
      variantSelector: variantSelectorMapper({ variantSelector, productData }),
    }),
    productId,
    whereToBuyData: {
      barcodeNumber,
      isPriceSpiderAvailable,
      isAdimoAvailable,
      adimoKey,
      adimoButton,
      shoppableInformationText: pluginInformationText,
      isMikMakAvailable,
      mikmakAppId,
      mikmakButton,
    },
  };
};

export const productPageHeaderSplitterMapper = (
  {
    productPageHeaderWithSplitter: {
      breadcrumbsUI,
      showBreadcrumbs,
      splitter,
      mode,
    },
    fields,
  }: ProductPageHeaderWithSplitter,
  headerSplitterAdditionalData?: HeaderSplitterAdditionalData
): ProductPageHeaderProps & { splitter: React.JSX.Element | undefined } => {
  const { productData, breadcrumbs } = fields;

  const { productTitle } = productData;

  return {
    ...productPageHeaderCommonMapper({
      breadcrumbsUI,
      breadcrumbs,
      showBreadcrumbs,
      mode,
      productTitle,
    }),
    splitter: splitter[0] ? (
      <Splitter {...splitter[0]} {...headerSplitterAdditionalData} />
    ) : undefined,
  };
};

export const productPageHeaderMapper = ({
  productPageHeaderBasic,
  productPageHeaderSplitter,
  headerSplitterAdditionalData,
}: {
  productPageHeaderBasic?: ProductPageHeaderBasic | null;
  productPageHeaderSplitter?: ProductPageHeaderWithSplitter | null;
  headerSplitterAdditionalData?: HeaderSplitterAdditionalData;
}): ProductPageHeaderProps | null => {
  if (productPageHeaderSplitter) {
    return productPageHeaderSplitterMapper(
      productPageHeaderSplitter,
      headerSplitterAdditionalData
    );
  }

  if (productPageHeaderBasic)
    return productPageHeaderBasicMapper(productPageHeaderBasic);

  return null;
};
